<script setup lang="ts" generic="TData extends Record<string, any>">
import { tv } from "tailwind-variants";

defineProps<{ columns: TableColumn<TData>[]; data: TData[] }>();

// Inline style for dynamic width/min/max
const getStyle = (column: TableColumn<TData>) => ({
  width: column.width,
  minWidth: column.minWidth,
  maxWidth: column.maxWidth,
});

// Tailwind Variants setup with boolean flags
const variants = tv({
  base: "flex",
  variants: {
    horizontalAlign: {
      left: "justify-start text-left",
      center: "justify-center text-center",
      right: "justify-end text-right",
    },
    verticalAlign: {
      top: "md:items-start",
      middle: "md:items-center",
      bottom: "md:items-end",
    },
    mobileHidden: {
      true: "hidden md:flex",
      false: "flex",
    },
    mobileFullWidth: {
      true: "flex-col md:flex-row",
      false: "flex-row",
    },
    fixed: {
      true: "flex-none",
      false: "flex-1",
    },
  },
  defaultVariants: {
    horizontalAlign: "left",
    verticalAlign: "top",
    mobileHidden: false,
    mobileFullWidth: false,
    fixed: false,
  },
});
</script>

<template>
  <div class="flex w-full flex-col">
    <!-- Header Row (hidden on mobile) -->
    <div class="hidden flex-row gap-2 border-b-2 py-2 font-semibold md:flex">
      <div
        v-for="column in columns"
        :key="column.key"
        :class="variants({ ...column, mobileFullWidth: false, fixed: !!column.width })"
        :style="getStyle(column)"
      >
        <slot :name="`${column.key}-header`">
          {{ column.label }}
        </slot>
      </div>
    </div>
    <!-- Data Rows -->
    <div
      v-for="(row, rowIndex) in data"
      :key="rowIndex"
      class="flex flex-col gap-2 border-b-2 py-4 md:flex-row"
    >
      <div
        v-for="column in columns"
        :key="column.key"
        :class="variants({ ...column, fixed: !!column.width })"
        :style="getStyle(column)"
      >
        <!-- Mobile label -->
        <span v-if="column.mobileFullWidth" class="block text-sm font-semibold text-muted md:hidden">
          {{ column.label }}
        </span>
        <!-- Cell content -->
        <slot :name="`${column.key}-cell`" :row="row">
          {{ row[column.key] || '' }}
        </slot>
      </div>
    </div>
  </div>
</template>
